import type { EndReason } from '../sim/run';
import { t } from './i18n';

function el<T extends HTMLElement = HTMLElement>(selector: string): T {
  const found = document.querySelector<T>(selector);
  if (!found) throw new Error(`Missing element ${selector}`);
  return found;
}

export interface SummaryData {
  reason: EndReason;
  distance: number;
  best: number;
  newBest: boolean;
  coins: number;
  flips: number;
  /** Wallet total after this run's coins were banked. */
  wallet: number;
}

/** Pause and run-summary overlays. Buttons dispatch intents; game rules stay in the sim. */
export class Overlays {
  private readonly pause = el('#pause-overlay');
  private readonly summary = el('#summary-overlay');
  onResume: () => void = () => {};
  onRestart: () => void = () => {};
  onPauseRequest: () => void = () => {};
  onGarage: () => void = () => {};
  onMap: () => void = () => {};
  private lastSummary: SummaryData | null = null;

  constructor() {
    el('#pause-button').addEventListener('click', () => this.onPauseRequest());
    el('#resume-button').addEventListener('click', () => this.onResume());
    el('#restart-button').addEventListener('click', () => this.onRestart());
    el('#retry-button').addEventListener('click', () => this.onRestart());
    el('#summary-garage').addEventListener('click', () => this.onGarage());
    el('#summary-map').addEventListener('click', () => this.onMap());
  }

  get pauseVisible(): boolean {
    return !this.pause.hidden;
  }

  get summaryVisible(): boolean {
    return !this.summary.hidden;
  }

  showPause(visible: boolean): void {
    this.pause.hidden = !visible;
    if (visible) el('#resume-button').focus();
  }

  /** Re-renders the visible summary (after a language change). */
  refreshSummary(): void {
    if (this.lastSummary && this.summaryVisible) this.showSummary(this.lastSummary);
  }

  showSummary(data: SummaryData): void {
    this.lastSummary = data;
    const cause = el('#summary-cause');
    cause.textContent = data.reason === 'crash' ? t('cause.crash') : t('cause.fuel');
    cause.classList.toggle('is-fuel', data.reason === 'out_of_fuel');
    el('#summary-distance').textContent = String(Math.floor(data.distance));
    el('#summary-new-best').hidden = !data.newBest;
    el('#summary-best').textContent = `${Math.floor(data.best)} m`;
    el('#summary-coins').textContent = String(data.coins);
    el('#summary-flips').textContent = String(data.flips);
    el('#summary-wallet').textContent = String(data.wallet);
    this.summary.hidden = false;
    el('#retry-button').focus();
  }

  hideAll(): void {
    this.pause.hidden = true;
    this.summary.hidden = true;
  }
}
